import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronDown, X } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';

/**
 * Multi-select chip picker backed by the /api/service-taxonomy/{kind} master
 * list. `kind` is one of: 'certifications' | 'industries' | 'applications'.
 * Selected values are canonical strings (the `name` field), not IDs — so
 * they stay stable even if an admin reorders the master list.
 */
const MasterMultiSelect = ({
  testid,
  label,
  hint,
  kind,
  value = [],
  onChange,
  placeholder = 'Click to select…',
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/service-taxonomy/${kind}`)
      .then(({ data }) => { if (alive) setOptions(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setOptions([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [kind]);

  const selected = new Set(value || []);

  const toggle = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onChange(Array.from(next));
  };

  const remove = (name) => {
    onChange((value || []).filter(v => v !== name));
  };

  return (
    <div data-testid={testid}>
      {label && (
        <Label className='text-xs font-medium text-muted-foreground'>{label}</Label>
      )}
      <div className='mt-1'>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type='button'
              className='w-full flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm text-left hover:bg-secondary/40'
              data-testid={`${testid}-trigger`}
            >
              <span className='text-muted-foreground'>
                {value.length === 0 ? placeholder : `${value.length} selected`}
              </span>
              <ChevronDown className='h-4 w-4 text-muted-foreground' />
            </button>
          </PopoverTrigger>
          <PopoverContent className='w-72 p-0' align='start'>
            <div className='max-h-64 overflow-y-auto py-1'>
              {loading ? (
                <div className='px-3 py-2 text-xs text-muted-foreground'>Loading…</div>
              ) : options.length === 0 ? (
                <div className='px-3 py-2 text-xs text-muted-foreground'>No options available</div>
              ) : (
                options.map(o => {
                  const on = selected.has(o.name);
                  return (
                    <button
                      key={o.id}
                      type='button'
                      onClick={() => toggle(o.name)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-secondary ${on ? 'bg-secondary/60' : ''}`}
                      data-testid={`${testid}-option-${o.id}`}
                    >
                      <span className={`h-4 w-4 shrink-0 rounded border flex items-center justify-center ${on ? 'bg-primary border-primary text-primary-foreground' : ''}`}>
                        {on && <Check className='h-3 w-3' />}
                      </span>
                      <span className='flex-1 truncate'>{o.name}</span>
                    </button>
                  );
                })
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {value.length > 0 && (
        <div className='mt-2 flex flex-wrap gap-1.5'>
          {value.map(v => (
            <Badge
              key={v}
              variant='secondary'
              className='pl-2 pr-1 py-0.5 gap-1'
              data-testid={`${testid}-chip-${v}`}
            >
              {v}
              <button
                type='button'
                onClick={() => remove(v)}
                className='rounded-full hover:bg-background/50 p-0.5'
                aria-label={`Remove ${v}`}
              >
                <X className='h-3 w-3' />
              </button>
            </Badge>
          ))}
        </div>
      )}
      {hint && <div className='text-[11px] text-muted-foreground mt-1'>{hint}</div>}
    </div>
  );
};

export default MasterMultiSelect;
